import React, { useEffect, useState, useRef, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, imageUrl, formatRupiah, formatDiscount, errorMessage } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { getStatus, STATUS_BADGE_CLASSES } from "../lib/status";
import QRDialog from "../components/QRDialog";
import {
    Plus,
    LogOut,
    Search,
    Trash2,
    Pencil,
    QrCode,
    Copy,
    Upload,
    FileSpreadsheet,
    Package,
    Loader2,
    Image as ImageIcon,
    Download,
    FileDown,
    Settings as SettingsIcon,
    MessageCircle,
    Users as UsersIcon,
    BookOpen,
    Printer,
    ShoppingBag,
    ShoppingCart,
} from "lucide-react";

export default function AdminDashboard() {
    const { user, logout } = useAuth();
    const isSuper = user?.role === "super_admin";
    const navigate = useNavigate();
    const [products, setProducts] = useState([]);
    const [q, setQ] = useState("");
    const [loading, setLoading] = useState(true);
    const [qrFor, setQrFor] = useState(null);
    const [importing, setImporting] = useState(false);
    const [importResult, setImportResult] = useState(null);
    const [selectedIds, setSelectedIds] = useState(new Set());
    const [qrPdfRepeat, setQrPdfRepeat] = useState(1);
    const [qrPdfLoading, setQrPdfLoading] = useState(false);
    const fileRef = useRef(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/products", { params: q ? { q } : {} });
            setProducts(data);
        } catch (error) {
            console.error("Failed to load products:", error);
        } finally {
            setLoading(false);
        }
    }, [q]);

    useEffect(() => {
        const t = setTimeout(load, 200);
        return () => clearTimeout(t);
    }, [load]);

    const handleDelete = async (p) => {
        if (!window.confirm(`Hapus produk "${p.name}"?`)) return;
        try {
            await api.delete(`/admin/products/${p.id}`);
            setProducts((arr) => arr.filter((x) => x.id !== p.id));
        } catch (e) {
            alert(errorMessage(e));
        }
    };

    const handleDuplicate = async (p) => {
        try {
            const { data } = await api.post(`/admin/products/${p.id}/duplicate`);
            // Navigate to edit form so admin can immediately tweak the copy
            navigate(`/admin/products/${data.id}`);
        } catch (e) {
            alert(errorMessage(e));
        }
    };

    const handleExcelChoose = () => fileRef.current?.click();

    const handleExcelUpload = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        e.target.value = "";
        setImporting(true);
        setImportResult(null);
        try {
            const fd = new FormData();
            fd.append("file", file);
            fd.append("mode", "upsert");
            const { data } = await api.post("/admin/excel/import", fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            setImportResult(data);
            await load();
        } catch (err) {
            setImportResult({ created: 0, updated: 0, errors: [errorMessage(err)] });
        } finally {
            setImporting(false);
        }
    };

    const downloadTemplate = async () => {
        try {
            const res = await api.get("/admin/excel/template", { responseType: "blob" });
            const url = URL.createObjectURL(res.data);
            const a = document.createElement("a");
            a.href = url;
            a.download = "template-produk.xlsx";
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(url);
        } catch (e) {
            alert(errorMessage(e));
        }
    };

    const exportAllProducts = async () => {
        try {
            const res = await api.get("/admin/excel/export", { responseType: "blob" });
            const url = URL.createObjectURL(res.data);
            const a = document.createElement("a");
            const stamp = new Date().toISOString().slice(0, 16).replace(/[:T]/g, "-");
            a.href = url;
            a.download = `produk-${stamp}.xlsx`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(url);
        } catch (e) {
            alert(errorMessage(e));
        }
    };

    const handleLogout = async () => {
        await logout();
        navigate("/admin/login");
    };

    const toggleSelect = (id) => {
        setSelectedIds((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id); else next.add(id);
            return next;
        });
    };
    const toggleSelectAll = () => {
        if (selectedIds.size === products.length && products.length > 0) {
            setSelectedIds(new Set());
        } else {
            setSelectedIds(new Set(products.map((p) => p.id)));
        }
    };
    const clearSelection = () => setSelectedIds(new Set());

    const downloadQrPdf = async () => {
        if (selectedIds.size === 0) return;
        setQrPdfLoading(true);
        try {
            const baseUrl = window.location.origin;
            const res = await api.post(
                "/admin/products/qr-pdf",
                { product_ids: Array.from(selectedIds), base_url: baseUrl, repeat: qrPdfRepeat },
                { responseType: "blob" },
            );
            const url = URL.createObjectURL(res.data);
            const a = document.createElement("a");
            const stamp = new Date().toISOString().slice(0, 16).replace(/[:T]/g, "-");
            a.href = url;
            a.download = `qr-stickers-${stamp}.pdf`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(url);
        } catch (e) {
            alert(errorMessage(e));
        } finally {
            setQrPdfLoading(false);
        }
    };

    return (
        <div data-testid="admin-dashboard-page" className="min-h-screen bg-background">
            <div className="border-b border-border bg-white">
                <div className="mx-auto flex max-w-7xl items-center justify-between px-5 py-4">
                    <div>
                        <span className="label-eyebrow text-muted-foreground">Dashboard</span>
                        <h1 className="font-heading text-2xl font-semibold tracking-tight">Manajemen Produk</h1>
                    </div>
                    <div className="flex items-center gap-3">
                        <div className="hidden text-right sm:block">
                            <div className="text-sm font-medium">{user?.name || user?.email}</div>
                            <div className="text-xs text-muted-foreground">{user?.role}</div>
                        </div>
                        <Link
                            to="/admin/orders"
                            data-testid="admin-orders-link"
                            className={`inline-flex items-center gap-2 rounded-full border border-border bg-white px-4 py-2 text-sm hover:bg-secondary transition-all duration-200 ${isSuper ? "" : "hidden"}`}
                        >
                            <ShoppingBag size={14} strokeWidth={1.75} /> Order
                        </Link>
                        <Link
                            to="/admin/conversations"
                            data-testid="admin-conversations-link"
                            className="inline-flex items-center gap-2 rounded-full border border-border bg-white px-4 py-2 text-sm hover:bg-secondary transition-all duration-200"
                        >
                            <MessageCircle size={14} strokeWidth={1.75} /> CRM Chat
                        </Link>
                        <Link
                            to="/admin/users"
                            data-testid="admin-users-link"
                            className={`inline-flex items-center gap-2 rounded-full border border-border bg-white px-4 py-2 text-sm hover:bg-secondary transition-all duration-200 ${isSuper ? "" : "hidden"}`}
                        >
                            <UsersIcon size={14} strokeWidth={1.75} /> Kelola User
                        </Link>
                        <Link
                            to="/admin/knowledge"
                            data-testid="admin-knowledge-link"
                            className={`inline-flex items-center gap-2 rounded-full border border-border bg-white px-4 py-2 text-sm hover:bg-secondary transition-all duration-200 ${isSuper ? "" : "hidden"}`}
                        >
                            <BookOpen size={14} strokeWidth={1.75} /> Knowledge
                        </Link>
                        <Link
                            to="/admin/pos"
                            data-testid="admin-pos-link"
                            className={`inline-flex items-center gap-2 rounded-full bg-primary text-primary-foreground px-4 py-2 text-sm hover:opacity-90 transition-all duration-200 ${isSuper ? "" : "hidden"}`}
                        >
                            <ShoppingCart size={14} strokeWidth={1.75} /> POS / Penjualan
                        </Link>
                        <Link
                            to="/admin/settings"
                            data-testid="admin-settings-link"
                            className={`inline-flex items-center gap-2 rounded-full border border-border bg-white px-4 py-2 text-sm hover:bg-secondary transition-all duration-200 ${isSuper ? "" : "hidden"}`}
                        >
                            <SettingsIcon size={14} strokeWidth={1.75} /> Pengaturan
                        </Link>
                        <button
                            data-testid="admin-logout-btn"
                            onClick={handleLogout}
                            className="inline-flex items-center gap-2 rounded-full border border-border bg-white px-4 py-2 text-sm hover:bg-secondary transition-all duration-200"
                        >
                            <LogOut size={14} strokeWidth={1.75} /> Keluar
                        </button>
                    </div>
                </div>
            </div>

            <div className="mx-auto max-w-7xl px-5 py-8 space-y-8">
                {/* Excel area — Super Admin only */}
                {isSuper && (
                <section className="grid gap-4 lg:grid-cols-3">
                    <div
                        data-testid="excel-upload-area"
                        className="lg:col-span-2 rounded-xl border-2 border-dashed border-border bg-white p-8 sm:p-12 flex flex-col sm:flex-row items-center gap-6"
                    >
                        <div className="grid h-14 w-14 shrink-0 place-items-center rounded-lg bg-accent/10 text-accent">
                            <FileSpreadsheet size={26} strokeWidth={1.5} />
                        </div>
                        <div className="flex-1 text-center sm:text-left">
                            <h2 className="font-heading text-xl font-semibold">Bulk Update via Excel</h2>
                            <p className="mt-1 text-sm text-muted-foreground">
                                Workflow disarankan untuk ratusan produk: <strong>Export → Edit di Excel → Upload kembali</strong>.
                                Kolom <code className="rounded bg-secondary px-1">status</code> menerima nilai: <em>ready</em>, <em>inden</em>, <em>habis</em>, <em>discontinued</em>
                                (atau bahasa Indonesia: ready stock / preorder / kosong / diskontinyu). Sistem cocokkan baris via <code className="rounded bg-secondary px-1">id</code>, lalu <code className="rounded bg-secondary px-1">sku</code>.
                            </p>
                            <div className="mt-4 flex flex-wrap gap-2 justify-center sm:justify-start">
                                <button
                                    data-testid="export-excel-btn"
                                    onClick={exportAllProducts}
                                    className="inline-flex items-center gap-2 rounded-full bg-accent px-5 py-2.5 text-sm font-medium text-accent-foreground hover:opacity-90 transition-all duration-200"
                                >
                                    <Download size={14} strokeWidth={1.75} /> Export Semua Produk
                                </button>
                                <button
                                    data-testid="upload-excel-btn"
                                    onClick={handleExcelChoose}
                                    disabled={importing}
                                    className="inline-flex items-center gap-2 rounded-full bg-primary px-5 py-2.5 text-sm font-medium text-primary-foreground hover:opacity-90 transition-all duration-200 disabled:opacity-50"
                                >
                                    {importing ? <Loader2 className="animate-spin" size={14} /> : <Upload size={14} strokeWidth={1.75} />}
                                    {importing ? "Mengunggah…" : "Upload Excel"}
                                </button>
                                <button
                                    data-testid="download-template-btn"
                                    onClick={downloadTemplate}
                                    className="inline-flex items-center gap-2 rounded-full border border-border bg-white px-5 py-2.5 text-sm font-medium hover:bg-secondary transition-all duration-200"
                                >
                                    <FileDown size={14} strokeWidth={1.75} /> Template Kosong
                                </button>
                            </div>
                            <input
                                ref={fileRef}
                                type="file"
                                accept=".xlsx,.xls"
                                className="hidden"
                                onChange={handleExcelUpload}
                                data-testid="excel-file-input"
                            />
                            {importResult && (
                                <div className="mt-4 rounded-md bg-secondary px-4 py-3 text-sm" data-testid="excel-import-result">
                                    <div>
                                        <span className="font-medium text-success">{importResult.created}</span> dibuat,{" "}
                                        <span className="font-medium">{importResult.updated}</span> diperbarui
                                        {importResult.errors?.length > 0 && (
                                            <span className="text-destructive">, {importResult.errors.length} error</span>
                                        )}
                                    </div>
                                    {importResult.errors?.length > 0 && (
                                        <ul className="mt-2 max-h-24 overflow-auto list-disc pl-5 text-xs text-destructive">
                                            {importResult.errors.slice(0, 8).map((er) => (
                                                <li key={er}>{er}</li>
                                            ))}
                                        </ul>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                    <div className="rounded-xl border border-border bg-[hsl(var(--muted))] p-6 flex flex-col justify-between">
                        <div>
                            <span className="label-eyebrow text-accent">Statistik</span>
                            <p className="mt-2 font-heading text-4xl font-semibold tracking-tight">{products.length}</p>
                            <p className="text-sm text-muted-foreground">produk di katalog</p>
                        </div>
                        <Link
                            to="/admin/products/new"
                            data-testid="add-product-link"
                            className="mt-4 inline-flex items-center gap-2 rounded-full bg-accent px-4 py-2.5 text-sm font-medium text-accent-foreground hover:opacity-90 transition-all duration-200"
                        >
                            <Plus size={14} strokeWidth={1.75} /> Tambah Produk Manual
                        </Link>
                    </div>
                </section>
                )}

                {/* Search */}
                <section className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <div>
                        <h2 className="font-heading text-xl font-semibold">Daftar Produk</h2>
                        <p className="text-sm text-muted-foreground">{isSuper ? "Kelola, generate QR, atau hapus produk." : "Edit detail & upload gambar produk."}</p>
                    </div>
                    <div className="relative w-full sm:max-w-xs">
                        <Search
                            size={16}
                            strokeWidth={1.6}
                            className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-muted-foreground"
                        />
                        <input
                            data-testid="admin-search-input"
                            value={q}
                            onChange={(e) => setQ(e.target.value)}
                            placeholder="Cari nama / SKU…"
                            className="w-full rounded-full border border-border bg-white py-2.5 pl-10 pr-4 text-sm outline-none focus:border-primary transition-colors duration-200"
                        />
                    </div>
                </section>

                {/* Selection action bar — visible when items selected */}
                {selectedIds.size > 0 && (
                    <section
                        data-testid="selection-toolbar"
                        className="rounded-xl border border-accent/40 bg-accent/10 px-4 py-3 flex items-center gap-4 flex-wrap"
                    >
                        <div className="text-sm flex-1">
                            <span className="font-semibold text-accent" data-testid="selection-count">{selectedIds.size}</span>{" "}
                            <span className="text-muted-foreground">produk terpilih</span>
                        </div>
                        <label className="text-xs text-muted-foreground inline-flex items-center gap-1.5">
                            Stiker per produk:
                            <input
                                data-testid="qr-pdf-repeat-input"
                                type="number"
                                min="1"
                                max="80"
                                value={qrPdfRepeat}
                                onChange={(e) => setQrPdfRepeat(Math.max(1, Math.min(80, Number(e.target.value) || 1)))}
                                className="ml-1 w-16 rounded-md border border-border bg-white px-2 py-1 text-sm outline-none focus:border-accent"
                            />
                        </label>
                        <button
                            data-testid="qr-pdf-download-btn"
                            onClick={downloadQrPdf}
                            disabled={qrPdfLoading}
                            className="inline-flex items-center gap-2 rounded-full bg-accent px-4 py-2 text-sm font-medium text-accent-foreground hover:opacity-90 transition disabled:opacity-50"
                        >
                            {qrPdfLoading
                                ? <Loader2 className="animate-spin" size={14} />
                                : <Printer size={14} strokeWidth={1.75} />}
                            Cetak QR PDF (A4)
                        </button>
                        <button
                            data-testid="clear-selection-btn"
                            onClick={clearSelection}
                            className="text-xs text-muted-foreground hover:text-foreground underline-offset-2 hover:underline"
                        >
                            Batal pilih
                        </button>
                    </section>
                )}

                {/* Table */}
                <section className="rounded-xl border border-border bg-white overflow-hidden">
                    {loading ? (
                        <div className="p-10 flex justify-center">
                            <Loader2 className="animate-spin text-primary" size={22} />
                        </div>
                    ) : products.length === 0 ? (
                        <div className="p-12 text-center" data-testid="admin-products-empty">
                            <Package size={32} className="mx-auto text-muted-foreground" strokeWidth={1.25} />
                            <p className="mt-3 font-medium">Belum ada produk</p>
                            <p className="text-sm text-muted-foreground">Upload Excel atau tambah manual untuk memulai.</p>
                        </div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm" data-testid="products-table">
                                <thead>
                                    <tr className="border-b border-border bg-[hsl(var(--muted))] text-left text-xs uppercase tracking-[0.12em] text-muted-foreground">
                                        <th className="px-3 py-3 font-medium w-10">
                                            <input
                                                type="checkbox"
                                                data-testid="select-all-checkbox"
                                                aria-label="Pilih semua"
                                                checked={products.length > 0 && selectedIds.size === products.length}
                                                onChange={toggleSelectAll}
                                                className="h-4 w-4 accent-accent cursor-pointer"
                                            />
                                        </th>
                                        <th className="px-4 py-3 font-medium">Produk</th>
                                        <th className="px-4 py-3 font-medium">SKU</th>
                                        <th className="px-4 py-3 font-medium">Kategori</th>
                                        <th className="px-4 py-3 font-medium">Status</th>
                                        <th className="px-4 py-3 font-medium text-right">Stok</th>
                                        <th className="px-4 py-3 font-medium text-right">Harga</th>
                                        <th className="px-4 py-3 font-medium text-right">Aksi</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {products.map((p) => (
                                        <tr
                                            key={p.id}
                                            data-testid={`product-row-${p.id}`}
                                            className={`border-b border-border last:border-b-0 hover:bg-[hsl(var(--muted))]/60 transition-colors duration-200 ${selectedIds.has(p.id) ? "bg-accent/5" : ""}`}
                                        >
                                            <td className="px-3 py-3">
                                                <input
                                                    type="checkbox"
                                                    data-testid={`select-checkbox-${p.id}`}
                                                    aria-label={`Pilih ${p.name}`}
                                                    checked={selectedIds.has(p.id)}
                                                    onChange={() => toggleSelect(p.id)}
                                                    className="h-4 w-4 accent-accent cursor-pointer"
                                                />
                                            </td>
                                            <td className="px-4 py-3">
                                                <div className="flex items-center gap-3">
                                                    <div className="h-11 w-11 shrink-0 overflow-hidden rounded-md bg-secondary">
                                                        {p.image_path ? (
                                                            <img src={imageUrl(p.image_path)} alt={p.name} className="h-full w-full object-cover" />
                                                        ) : (
                                                            <div className="grid h-full place-items-center text-muted-foreground">
                                                                <ImageIcon size={16} strokeWidth={1.25} />
                                                            </div>
                                                        )}
                                                    </div>
                                                    <div className="min-w-0">
                                                        <Link
                                                            to={`/admin/products/${p.id}`}
                                                            className="block truncate font-medium text-foreground hover:text-accent transition-colors"
                                                        >
                                                            {p.name}
                                                        </Link>
                                                        {p.variants?.length > 0 && (
                                                            <div className="truncate text-xs text-muted-foreground">{p.variants.length} varian</div>
                                                        )}
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="px-4 py-3 text-muted-foreground">{p.sku || "—"}</td>
                                            <td className="px-4 py-3 text-muted-foreground">{p.category || "—"}</td>
                                            <td className="px-4 py-3">
                                                {(() => {
                                                    const st = getStatus(p.status);
                                                    return (
                                                        <span
                                                            data-testid={`row-status-${p.id}`}
                                                            className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${STATUS_BADGE_CLASSES[st.tone]}`}
                                                        >
                                                            {st.label}
                                                        </span>
                                                    );
                                                })()}
                                            </td>
                                            <td className="px-4 py-3 text-right">
                                                <span
                                                    data-testid={`row-stock-${p.id}`}
                                                    className={`font-medium tabular-nums ${(p.stock_qty || 0) <= 0 ? "text-destructive" : (p.stock_qty || 0) < 5 ? "text-orange-600" : "text-foreground"}`}
                                                >
                                                    {p.stock_qty || 0}
                                                </span>
                                            </td>
                                            <td className="px-4 py-3 text-right">
                                                <div className="flex flex-col items-end leading-tight">
                                                    {p.list_price > p.price && p.discount_percent > 0 ? (
                                                        <>
                                                            <span className="text-[11px] text-muted-foreground line-through">{formatRupiah(p.list_price)}</span>
                                                            <span className="font-medium">{formatRupiah(p.price)}</span>
                                                            <span className="text-[10px] font-semibold text-accent">-{formatDiscount(p.discount_percent)}%</span>
                                                        </>
                                                    ) : (
                                                        <span className="font-medium">{formatRupiah(p.price)}</span>
                                                    )}
                                                </div>
                                            </td>
                                            <td className="px-4 py-3">
                                                <div className="flex items-center justify-end gap-1">
                                                    <button
                                                        data-testid={`product-qr-btn-${p.id}`}
                                                        onClick={() => setQrFor(p)}
                                                        title="QR Code"
                                                        className="inline-flex h-8 w-8 items-center justify-center rounded-md hover:bg-secondary text-foreground transition-colors"
                                                    >
                                                        <QrCode size={16} strokeWidth={1.6} />
                                                    </button>
                                                    <Link
                                                        to={`/admin/products/${p.id}`}
                                                        data-testid={`product-edit-link-${p.id}`}
                                                        title="Edit"
                                                        className="inline-flex h-8 w-8 items-center justify-center rounded-md hover:bg-secondary text-foreground transition-colors"
                                                    >
                                                        <Pencil size={16} strokeWidth={1.6} />
                                                    </Link>
                                                    <button
                                                        data-testid={`product-duplicate-btn-${p.id}`}
                                                        onClick={() => handleDuplicate(p)}
                                                        title="Duplikat produk"
                                                        className="inline-flex h-8 w-8 items-center justify-center rounded-md hover:bg-accent/10 text-accent transition-colors"
                                                    >
                                                        <Copy size={16} strokeWidth={1.6} />
                                                    </button>
                                                    <button
                                                        data-testid={`product-delete-btn-${p.id}`}
                                                        onClick={() => handleDelete(p)}
                                                        title="Hapus"
                                                        className={`inline-flex h-8 w-8 items-center justify-center rounded-md hover:bg-destructive/10 text-destructive transition-colors ${isSuper ? "" : "hidden"}`}
                                                    >
                                                        <Trash2 size={16} strokeWidth={1.6} />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </section>
            </div>

            {qrFor && <QRDialog product={qrFor} onClose={() => setQrFor(null)} />}
        </div>
    );
}
